/**
* Helper class for sizing bitmaps using 9slicing in relation to size of other content.
*
* A typical use would be a textpanel in which the textfields are dynamically sized, 
* and in which a background bitmap should be sized according to the textfields.
*
* This is a first version and is not extensively tested.
* Originally used in the '905-ING Culturefit' project. 
*  See app/src/assets/skins/EndAssets.fla:FeedbackPanel for asset example
*
* Uses an adapted version of createjs.ScaleBitmap, which is not part of standard createjs/easeljs builds!
* Original can be found at https://github.com/CreateJS/EaselJS/tree/master/extras
* 
* TO DO / WISHES
* - support for non-image 9slice targets by caching and using cachedCanvas as bitmap
* - support for explicitly set margins instead of based on content
* 
* ISSUES:
* - Container on timeline does not work well with frame changes.
*   The ScaleBitmap instance _bitmap ends up as top child, not bottom.
*   Call update() after frame change to work around this, if frame changes cannot be avoided.
* 
* Created on 30-mrt-2017, 10:17:54
* @author Reinier Feijen for &ranj
**/

import ScaleBitmap from "./ScaleBitmap";
import Point from "../../geom/Point";
import { isNull } from "../../util/TypeUtil";

export default class Slice9Panel {
    /**
     * 
     * @param {type} container The container which contents define the 9slice regions 
     * @param {type} bitmap The bitmap to 9slice, typically bottommost displayObject in container. 
     * @param {{mode: ScaleBitmap.BOTH, cacheBitmap: true, pixelPerfect: false, overlap: false }} options 
     * - mode: Type of 9slicing: Slice9Panel.HORIZONTAL, Slice9Panel.VERTICAL, Slice9Panel.BOTH (default);
     * - cacheBitmap: Flag indicating that bitmap should be cached after each update. Default true;
     * - pixelPerfect: Flag to indicate pixel perfect slicing/drawing (whole pixels). Default false;
     * - overlap: Flag to set the sliced regions to slightly overlap to avoid anti-aliasing artifacts. 
     * Therefore, the overlap does not work well with transparent images. Default false;
     */
    constructor(container, bitmap, options = {mode: ScaleBitmap.BOTH, cacheBitmap: true, pixelPerfect: false, overlap: false }) {
        this._container = container;
        //bitmap is child of container which typically functions as background
        //all other children of container are considered content of which the bounds will be used to resize the background
        //bitmap will be replaced by a bitmap which can be 9sliced
        var img = bitmap.image;
        //mode : Slice9Panel.HORIZONTAL/HEIGHT/BOTH (default), (implemented as bitmask)
        this._mode = options.mode || Slice9Panel.BOTH;
        //
        this._cacheBitmap = isNull(options.cacheBitmap) ? true : options.cacheBitmap;
        //Create a ScaleBitmap with scale9Grid based on other children of container
        var bc = this._getContentBounds(bitmap);
        var bct = this._localToLocalRect(bc, container, bitmap);//transformed into coord space of bitmap
        if (this._mode == Slice9Panel.HORIZONTAL) bct.height = 0;
        else if (this._mode == Slice9Panel.VERTICAL) bct.width = 0;
        var scaleBitmap = new ScaleBitmap(img, bct, options.pixelPerfect, options.overlap);
        //NB: flipped bitmaps in Adobe Animate do not have scaleX=1 but skewY=180:
        scaleBitmap.setTransform(bitmap.x, bitmap.y, bitmap.scaleX, bitmap.scaleY, bitmap.rotation, bitmap.skewX, bitmap.skewY, bitmap.regX, bitmap.regY);
        //replace bitmap with the scaleBitmap:
        var idx = bitmap.parent.getChildIndex(bitmap);
        bitmap.parent.addChildAt(scaleBitmap, idx);
        //Hide the original bitmap. 
        //We dont remove it, because if the container is on a timeline, framejumps to frames containing the container cause the bitmap to be re-added.
        bitmap.visible = false;
        //
        this._bitmap = scaleBitmap;
        //Store initial bounds to later be able to calc margins to apply to content
        this._contentBoundsInitial = bc.clone();
        this._bitmapBoundsInitial = bitmap.getBounds().clone();//NB: untranformed, not in container coord system!
        this._bitmapOffset = new Point(this._bitmap.x, this._bitmap.y);
        //console.log("Slice9Panel: ",bc.toString(), this._bitmapOffset.toString(),this._bitmap.x,this._bitmap.y,bitmap.x,bitmap.y)
        this._bitmap.setBounds(0, 0, this._bitmapBoundsInitial.width, this._bitmapBoundsInitial.height);
        //
        this._minWidth = null;
        this._minHeight = null;
    };


    /**
     * Set a minimum width for the bitmap.
     * If content+margins is less wide, this value will be used instead.
     * @param Number value Desired minimum width (in container coordinate space) for bitmap
     */
    setMinWidth(value) {
        this._minWidth = value;
        this.update();
    }
    setMinHeight(value) {
        this._minHeight = value;
        this.update();
    }


    /**
     * Update the size and position of the bitmap based on the (new) bounds of content
     * Call this whenever container contents changes
     */
    update() {
        var bc = this._getContentBounds();
        if (bc == null) return;
        var bc0 = this._contentBoundsInitial, bb0 = this._bitmapBoundsInitial;
        //
        var dw = bc.width - bc0.width, dh = bc.height - bc0.height;
        //console.log("Slice9Panel: ",bc.toString(), bc0.toString(), dx,dy,dw,dh);
        //apply 9slicing (in bitmap coord space)
        var w = bb0.width + dw * this._bitmap.scaleX;
        var h = bb0.height + dh * this._bitmap.scaleY;
        //
        var applyWidth = (this._mode & Slice9Panel.HORIZONTAL) == Slice9Panel.HORIZONTAL;
        var applyHeight = (this._mode & Slice9Panel.VERTICAL) == Slice9Panel.VERTICAL;
        //
        if (this._minWidth && w < this._minWidth * this._bitmap.scaleX) {
            var ow = w;
            w = this._minWidth * this._bitmap.scaleX;
            //adjust dx accordingly
            var dw = (w - ow) / this._bitmap.scaleX;
            var r = bc.x / bc.width;
            bc.x += r * dw;
        }
        if (this._minHeight && h < this._minHeight * this._bitmap.scaleY) {
            var oh = h;
            h = this._minHeight * this._bitmap.scaleY;
            //adjust dx accordingly
            var dh = (h - oh) / this._bitmap.scaleY;
            var r = bc.y / bc.height;
            bc.y += r * dh;
        }
        //TODO/WISH: dont do all calcs above if not needed?
        if (!applyWidth) {
            bc.width = bc0.width;
            bc.x = bc0.x;
            w = bb0.width;
        }
        if (!applyHeight) {
            bc.height = bc0.height;
            bc.y = bc0.y;
            h = bb0.height;
        }
        //reposition bitmap based on dx, dy of content
        var dx = bc.x - bc0.x, dy = bc.y - bc0.y;
        this._bitmap.x = this._bitmapOffset.x + dx;
        this._bitmap.y = this._bitmapOffset.y + dy;
        //
        this._bitmap.setDrawSize(w, h);
        this._bitmap.setBounds(0, 0, w, h);
        //Make sure _bitmap still is bottom most child. 
        //  This is not the case when framejumps on the parent of container have taken place. 
        //  Framejumps cause our _bitmap to be placed on top! (Or other children to be placed below)
        //  Better would be to detect when _bitmap is placed on top, but the "added" event is not dispatched due to the framejumps.
        this._bitmap.parent.setChildIndex(this._bitmap, 0);
        //cache bitmap to avoid redrawing the regions every frame
        if (this._cacheBitmap) {
            this._bitmap.uncache();
            this._bitmap.cache(0, 0, w, h);
        }
    }

    /**
     * Returns the change in height compared to original height.
     * Useful for vertical aligning after (text) changes.
     */
    getDeltaHeight() {
        var bc = this._getContentBounds();
        var bc0 = this._contentBoundsInitial;
        return bc.height - bc0.height;
    }

    /**
     * Returns combined bounds of all children of container except for background bitmap
     * Depends on all children of container having non-null bounds
     * See http://www.createjs.com/docs/easeljs/classes/DisplayObject.html#method_getBounds
     * @param Bitmap/ScaleBitmap except Optional displayobject to not take into account. If not specified, this._bitmap will be used.
     * @returns createjs.Rectangle
     */
    _getContentBounds(except) {
        if (this.debugShape) this.debugShape.visible = false;
        except = except || this._bitmap;
        except.visible = false;
        var b = this._container.getBounds();
        except.visible = true;
        if (this.debugShape) this.debugShape.visible = true;
        return b;
    }

    getBounds() {
        return this._container.getBounds();
    }

    // Helper functions:

    _localToLocalRect(rect, from, target, rectOut) {
        var p0 = from.localToLocal(rect.x, rect.y, target);
        var p1 = from.localToLocal(rect.x + rect.width, rect.y + rect.height, target);
        var r = rectOut || new createjs.Rectangle();
        r.setValues(Math.min(p0.x, p1.x), Math.min(p0.y, p1.y), Math.abs(p0.x - p1.x), Math.abs(p0.y - p1.y));
        return r;
    }
}

Slice9Panel.HORIZONTAL = 1;
Slice9Panel.VERTICAL = 2;
Slice9Panel.BOTH = 3;